import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { localizeBrand } from "../../utils/brandTerms";
import { Moon, Sparkles, Star, Sun } from "lucide-react";
import { Headline, useHeadlines } from "../../contexts/HeadlineContext";
import { useAppLanguage } from "../../utils/appLanguage";
import { SoftCard } from "./ui";
import type { ZodiacReading } from "./useZodiacToday";

const latestHeadline = (list: Headline[]) =>
  [...list].sort((a, b) => b.date.getTime() - a.date.getTime())[0] ?? null;

/** Today's Astrology + Daily Encouragement (moved here from the sidebar). */
const DailyInsights: React.FC<{ reading?: ZodiacReading | null }> = ({ reading }) => {
  const { t, i18n } = useTranslation();
  const { getHeadlineByDate, headlines } = useHeadlines();
  const myLang = useAppLanguage();
  const lastFetchKeyRef = useRef("");

  const [quote, setQuote] = useState<string>("");
  const [astro, setAstro] = useState({ luckyColor: "", energyLevel: "", goldenHour: "" });

  useEffect(() => {
    const load = async () => {
      try {
        const today = new Date();
        const fetchKey = `${today.toISOString().slice(0, 10)}:${myLang}`;
        if (lastFetchKeyRef.current === fetchKey) return;

        // The cron files each day's headline under the Kolkata calendar date.
        const kolkataDay = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(today);
        let data = await getHeadlineByDate(new Date(`${kolkataDay}T00:00:00.000Z`)).catch(() => null);
        // Today's message not generated yet: show the latest one that exists.
        if (!data) data = latestHeadline(headlines);
        if (!data) return;
        lastFetchKeyRef.current = fetchKey;
        const localized =
          myLang === "th" ? data.dailyMessage_in_thai : myLang === "ar" ? data.dailyMessage_in_arabic : "";
        setQuote(localized || data.dailyMessage);

        if (data?.lucky_color || data?.energy_level || data?.golden_hour || data?.color_code) {
          setAstro({
            luckyColor: data.lucky_color || "",
            energyLevel: data.energy_level || "",
            goldenHour: data.golden_hour || "",
          });
        }
      } catch (err) {
        console.error("Failed to load daily message:", err);
      }
    };
    load();
  }, [getHeadlineByDate, myLang, headlines]);

  const rows = [
    { icon: Star, label: t("chatInterface.luckyColor"), value: reading?.luckyColor || astro.luckyColor || "—" },
    { icon: Sun, label: t("chatInterface.energyLevel"), value: reading?.energyLevel || reading?.theme || astro.energyLevel || "—" },
    { icon: Moon, label: t("chatInterface.goldenHour"), value: reading?.luckyWindow || astro.goldenHour || "—" },
  ];

  const encouragement = reading?.dailyEncouragement || quote;

  return (
    <>
      <SoftCard>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-astria-muted">
          <Moon className="h-4 w-4" />
          {t("chatInterface.todaysAstrology")}
        </div>
        <div className="mt-4 grid gap-2"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
          {rows.map(({ icon: Icon, label, value }) => (
            <div
              key={label}
              className="flex min-w-0 items-center gap-3 px-3.5 py-3"
              style={{ background: "var(--astria-surface-soft)", borderRadius: "var(--astria-radius-input)" }}
            >
              <span
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
                style={{ background: "var(--astria-accent-glow)" }}
              >
                <Icon className="h-4 w-4" style={{ color: "var(--astria-accent-primary)" }} />
              </span>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-astria-muted">{label}</div>
                <div className="flex items-center gap-2 text-sm font-semibold text-astria-text">
                  <span className="break-words">{value}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SoftCard>

      {encouragement && (
      <SoftCard>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-astria-muted">
          <Sparkles className="h-4 w-4" />
          {t("sidebar.HeadlineTitle")}
        </div>
        <p className="mt-3 text-[15px] italic leading-relaxed text-astria-text">“{encouragement}”</p>
        <p className="mt-3 text-right text-xs font-medium text-astria-muted">{localizeBrand("— Astria", i18n.language)}</p>
      </SoftCard>
      )}
    </>
  );
};

export default DailyInsights;
